import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MusicNotesSimple, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || !password) return toast.error("Llena ambos campos");
    setLoading(true);
    try {
      const u = await login(phone, password);
      toast.success("¡Bienvenido de vuelta!");
      // Always reset scroll to top so the user sees the navbar on mobile
      window.scrollTo({ top: 0, behavior: "instant" });
      if (u?.is_admin) nav("/admin");
      else if (u?.song_credits > 0) nav("/create");
      else nav("/payment");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 py-10 flex-1 flex flex-col justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5 transition-colors"
          data-testid="login-back-home"
        >
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="login-brand">
          <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
            <MusicNotesSimple weight="fill" size={22} className="text-black" />
          </div>
          <span className="font-display font-bold text-2xl">Tu MusiKa</span>
        </Link>
        <h1 className="font-display text-3xl font-bold mb-1">Iniciar sesión</h1>
        <p className="text-white/50 mb-8">Bienvenido de regreso, artista.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Teléfono</label>
            <input
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+52 55 1234 5678"
              className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
              data-testid="login-phone"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 pr-12 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="login-toggle-password"
              >
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-50 text-black font-semibold transition-all active:scale-95"
            data-testid="login-submit"
          >
            {loading ? "Entrando…" : "Iniciar sesión"}
          </button>
        </form>
        <p className="text-center text-sm text-white/50 mt-6">
          ¿No tienes cuenta?{" "}
          <Link to="/register" className="text-[#FFB800] hover:underline" data-testid="login-go-register">
            Crear una
          </Link>
        </p>
      </div>
    </div>
  );
}
